import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormHelperText,
  InputAdornment,
  LinearProgress,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import {
  DEFAULT_VARIANT_TITLE,
  EXCHANGE_MODES,
  exchangeCreditMessage,
  exchangeVariantTitle,
} from "./options";
import { useExchangeProductSearch } from "../useExchangeCatalog";
import { formatMoney } from "../formatters";
import type { ClaimTranslator } from "../types";

type ExchangeVariant = {
  variant_id: string;
  title: string;
  price: number;
};

type ExchangeProduct = {
  product_id: string;
  title: string;
  image_url: string | null;
  variants: ExchangeVariant[];
};

type ExchangeModeToggleProps = {
  mode: string;
  sameItemAvailable: boolean;
  differentItemAllowed: boolean;
  onChange: (mode: string) => void;
  t: ClaimTranslator;
};

export const ExchangeModeToggle = ({
  mode,
  sameItemAvailable,
  differentItemAllowed,
  onChange,
  t,
}: ExchangeModeToggleProps) =>
  differentItemAllowed ? (
    <Box sx={{ mb: 2 }}>
      <ToggleButtonGroup
        exclusive
        fullWidth
        size="small"
        color="primary"
        value={mode}
        onChange={(_, value) => value && onChange(value)}
      >
        <ToggleButton
          value={EXCHANGE_MODES.SAME_ITEM}
          disabled={!sameItemAvailable}
        >
          {t("resolve.form.exchangeSameItem")}
        </ToggleButton>
        <ToggleButton value={EXCHANGE_MODES.DIFFERENT_ITEM}>
          {t("resolve.form.exchangeDifferentItem")}
        </ToggleButton>
      </ToggleButtonGroup>
      {!sameItemAvailable && (
        <FormHelperText>
          {t("resolve.form.exchangeSameItemUnavailable")}
        </FormHelperText>
      )}
    </Box>
  ) : null;

type ExchangeProductOptionProps = {
  product: ExchangeProduct;
  currency: string;
  credit: number | null;
  selectedVariantId: string;
  onSelect: (variantId: string, title: string) => void;
  t: ClaimTranslator;
};

const priceDifferenceLabel = (
  price: number,
  credit: number | null,
  currency: string,
  t: ClaimTranslator,
) => {
  if (credit == null || price <= credit) return "";
  const amount = formatMoney(price - credit, currency) ?? "";
  return ` (${t("resolve.form.exchangeUpcharge", { amount })})`;
};

const variantLabel = (
  variant: ExchangeVariant,
  credit: number | null,
  currency: string,
  t: ClaimTranslator,
) => {
  const price = `${formatMoney(variant.price, currency) ?? ""}${priceDifferenceLabel(
    variant.price,
    credit,
    currency,
    t,
  )}`;
  if (variant.title === DEFAULT_VARIANT_TITLE) return price;
  return `${variant.title} · ${price}`;
};

const ExchangeProductOption = ({
  product,
  currency,
  credit,
  selectedVariantId,
  onSelect,
  t,
}: ExchangeProductOptionProps) => (
  <Box
    sx={{
      display: "flex",
      gap: 2,
      p: 2,
      borderBottom: "1px solid",
      borderColor: "divider",
    }}
  >
    {product.image_url && (
      <Box
        component="img"
        src={product.image_url}
        alt={product.title}
        sx={{ width: 56, height: 56, objectFit: "cover", borderRadius: 1 }}
      />
    )}
    <Box>
      <Typography sx={{ fontWeight: 500, mb: 1 }}>{product.title}</Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {product.variants.map((variant) => {
          const isSelected = variant.variant_id === selectedVariantId;
          return (
            <Chip
              key={variant.variant_id}
              label={variantLabel(variant, credit, currency, t)}
              color={isSelected ? "primary" : "default"}
              variant={isSelected ? "filled" : "outlined"}
              onClick={() =>
                onSelect(
                  variant.variant_id,
                  exchangeVariantTitle(product.title, variant.title),
                )
              }
            />
          );
        })}
      </Box>
    </Box>
  </Box>
);

type ExchangeProductPickerProps = {
  shipInsureId: string;
  lineItemId: string;
  value: string;
  selectedTitle: string;
  hasError: boolean;
  onSelect: (variantId: string, title: string) => void;
  t: ClaimTranslator;
};

export const ExchangeProductPicker = ({
  shipInsureId,
  lineItemId,
  value,
  selectedTitle,
  hasError,
  onSelect,
  t,
}: ExchangeProductPickerProps) => {
  const [query, setQuery] = useState("");
  const {
    products,
    credit,
    currency,
    allowHigherPriced,
    allowLowerPriced,
    maxUpcharge,
    nextCursor,
    status,
    loadMore,
  } = useExchangeProductSearch(shipInsureId, lineItemId, query);
  const isEmpty = status === "ready" && products.length === 0;
  const creditMessage = t(
    exchangeCreditMessage({ allowHigherPriced, allowLowerPriced, maxUpcharge }),
    {
      credit: formatMoney(credit, currency) ?? "",
      cap: formatMoney(maxUpcharge, currency) ?? "",
    },
  );

  return (
    <Box>
      <TextField
        fullWidth
        size="small"
        label={t("resolve.form.exchangeSearch")}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />
      {credit != null && (
        <FormHelperText>
          {allowHigherPriced && !allowLowerPriced
            ? `${creditMessage} ${t("resolve.form.exchangeCheaperHidden")}`
            : creditMessage}
        </FormHelperText>
      )}
      {selectedTitle && (
        <Typography
          sx={{ mt: 1, fontWeight: 500 }}
          color={hasError ? "error" : "primary"}
        >
          {t("resolve.form.exchangeSelected", { title: selectedTitle })}
        </Typography>
      )}
      <Box
        sx={{
          mt: 1,
          maxHeight: 360,
          overflow: "auto",
          border: "1px solid",
          borderColor: hasError ? "error.main" : "divider",
          borderRadius: 2,
          backgroundColor: "background.paper",
        }}
      >
        {status === "loading" && <LinearProgress />}
        {products.map((product: ExchangeProduct) => (
          <ExchangeProductOption
            key={product.product_id}
            product={product}
            currency={currency}
            credit={credit}
            selectedVariantId={value}
            onSelect={onSelect}
            t={t}
          />
        ))}
        {isEmpty && (
          <Typography sx={{ p: 2 }} color="text.secondary">
            {t("resolve.form.exchangeNoResults")}
          </Typography>
        )}
        {nextCursor && status !== "loading" && (
          <Button fullWidth onClick={loadMore}>
            {t("resolve.form.exchangeLoadMore")}
          </Button>
        )}
      </Box>
      {status === "error" && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {t("resolve.form.exchangeCatalogError")}
        </Alert>
      )}
    </Box>
  );
};
